/* eslint-disable no-case-declarations */
/* eslint-disable max-len */
/* eslint-disable no-console */
import React, { useContext, useState, useEffect, useCallback, useMemo } from 'react';
import { Box, Grid, Stack, Typography, IconButton, Button } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import DetailsIcon from '../../../../../../../../../assets/Resources/DetailIcon.svg';
import CategoriesIcon from '../../../../../../../../../assets/Resources/CategoriesIcon.svg';
import AcessIcon from '../../../../../../../../../assets/Resources/AccessIcon.svg';
import { IoPricetagOutline } from 'react-icons/io5';
import { FaRegEdit } from 'react-icons/fa';
import { PiClockCountdownBold } from 'react-icons/pi';
import { LanguageContext } from '../../../../../../../../../libs/Context';
import { PodcastListItem, PodcastItem, SeasonItem, EpisodeItem } from '../types';

// Import components from Reusable folder
import DetailsSlider from '../../../../Components/Reusable/RightBarOptions/Components/SliderContent/Components/DetailsSlider';
import CategorySlider from '../../../../Components/Reusable/RightBarOptions/Components/SliderContent/Components/CategorySlider';
import TagSlider from '../../../../Components/Reusable/RightBarOptions/Components/SliderContent/Components/TagsSlider';
import AccessSlider from '../../../../Components/Reusable/RightBarOptions/Components/SliderContent/Components/AccessSlider';
import ManageContentSlider from '../../../../Components/Reusable/RightBarOptions/Components/SliderContent/Components/ManageContentSlider';
import ActivitySlider from '../../../../Components/Reusable/RightBarOptions/Components/SliderContent/Components/ActivitySlider';
import useContentPermissions from '../../../Reusable/ContentCards/useContentPermissions';
import { selectedTabs } from '../../../../../constants';

interface PodcastDetailsSliderProps {
  selectedTab?: selectedTabs;
  setSelectedTab: (tab: selectedTabs) => void;
  setOpen: (open: boolean) => void;
  articleCreate?: string;
  podcastData?: any;
  selectedItems?: string[];
  podcastsList?: PodcastListItem[];
  companyId?: string;
  onSave?: (updatedData: any, podcastIds: string[]) => void;
  itemType?: 'podcast' | 'season' | 'episode';
}

// Define a consolidated UI state interface for podcast data
interface PodcastUIState {
  details: {
    title: string;
    description: string;
    sourceType: string;
    coverImage?: {
      url: string;
      canEdit?: boolean;
      canDelete?: boolean;
      source?: string;
      key?: string;
    };
  };
  categoryIds: string[];
  tags: string[];
  access: {
    // visibility: any;
    allowDownload: boolean;
    allowShare: boolean;
    // accessControl: {
    //   internalEmployees: {
    //     allSelected: boolean;
    //     departments: Array<{name: string; count?: number}>;
    //     specificUsers: Array<{userId: string; name?: string}>;
    //   };
    //   userGroups: Array<{type: string; count?: number}>;
    //   participantCount: number;
    //   participantLimit: number;
    // };
  };
  manage: {
    status: string;
    publishDate?: string;
  };
}

/**
 * PodcastDetailsSlider - A specialized slider for podcast content details
 */
const PodcastDetailsSlider: React.FC<PodcastDetailsSliderProps> = (props) =>
{
	const {
		selectedTab = 'Details',
		setSelectedTab,
		setOpen,
		articleCreate,
		podcastData,
		selectedItems = [],
		podcastsList = [],
		companyId,
		onSave,
		itemType = 'podcast'
	} = props;

	const { language } = useContext(LanguageContext);
	const { isPrivateShareEnabled, isShareEnabled, isDownloadEnabled } = useContentPermissions();

	// Define the tabs specific to podcast editing - memoized to prevent unnecessary re-creation
	const podcastTabs = useMemo(() =>
	{
		// For seasons and episodes, only show Details tab
		if (itemType === 'season' || itemType === 'episode')
		{
			return [
				{ name: 'Details', Icon: DetailsIcon, ImageIcon: true }
			];
		}

		// Base tabs that are always available for podcasts
		const baseTabs = [
			{ name: 'Categories', Icon: CategoriesIcon, ImageIcon: true },
			{ name: 'Add Tags', Icon: <IoPricetagOutline fontSize={'26px'} />, ImageIcon: false },
			{ name: 'Access', Icon: AcessIcon, ImageIcon: true }
			// { name: 'Activity', Icon: <PiClockCountdownBold fontSize={'26px'} />, ImageIcon: false }
		];

		// Only add the Details tab if a single podcast is selected
		if (!selectedItems || selectedItems.length <= 1)
		{
			return [
				{ name: 'Details', Icon: DetailsIcon, ImageIcon: true },
				...baseTabs
			];
		}

		return baseTabs;
	}, [ selectedItems, itemType ]);

	// Update selectedTab if Details is selected but no longer available
	useEffect(() =>
	{
		if (selectedTab === 'Details' && selectedItems && selectedItems.length > 1)
		{
			// Switch to first available tab if Details is not available
			if (podcastTabs.length > 0)
			{
				setSelectedTab(podcastTabs[0].name as selectedTabs);
			}
		}
	}, [ selectedItems, selectedTab, podcastTabs, setSelectedTab ]);

	// Simple function to create initial state from podcast data or empty defaults
	const createInitialState = useCallback((): PodcastUIState =>
	{
		// Handle coverImage based on itemType
		let coverImage;

		if (podcastData?.coverImage)
		{
			if (itemType === 'episode' && typeof podcastData.coverImage === 'string')
			{
				// For episodes, coverImage is a string URL
				coverImage = {
					url       : podcastData.coverImage,
					canEdit   : true,
					canDelete : false
				};
			}
			else if (typeof podcastData.coverImage === 'object' && podcastData.coverImage?.url)
			{
				// For podcasts and seasons, coverImage is an object
				coverImage = {
					url       : podcastData.coverImage.url,
					canEdit   : true,
					canDelete : false
				};
			}
		}

		return {
			details : {
				title       : podcastData?.title || '',
				description : podcastData?.description || '',
				sourceType  : podcastData?.sourceType || 'system',
				coverImage
			},
			categoryIds : podcastData?.categoryIds || [],
			tags        : podcastData?.tags || [],
			access      : {
				// visibility    : podcastData?.visibility !== undefined ? (podcastData?.visibility as 'public' | 'private') : isPrivateShareEnabled ? 'private' : 'public',
				allowDownload : podcastData?.allowDownload !== undefined ? podcastData?.allowDownload : isDownloadEnabled,
				allowShare    : podcastData?.allowShare !== undefined ? podcastData?.allowShare : isShareEnabled
				// accessControl : podcastData?.accessControl || ''
			},
			manage : {
				status      : podcastData?.status || '',
				publishDate : podcastData?.publishDate || ''
			}
		};
	}, [ podcastData, itemType ]);

	// Store original state for comparison
	const [ originalData, setOriginalData ] = useState<PodcastUIState | null>(null);

	// Simplified state management - just use the podcast data or empty defaults
	const [ uiState, setUiState ] = useState<PodcastUIState>(createInitialState);

	// Reset state when selection changes
	useEffect(() =>
	{
		const newState = createInitialState();

		setUiState(newState);
		setOriginalData(newState);
	}, [ podcastData, createInitialState ]);

	// Detect changes in state
	const hasChanges = useMemo(() =>
	{
		if (!originalData) return false;

		// Deep comparison of relevant fields
		return (
			originalData.details.title !== uiState.details.title ||
			originalData.details.description !== uiState.details.description ||
			originalData.details.coverImage !== uiState.details.coverImage ||
			JSON.stringify(originalData.categoryIds) !== JSON.stringify(uiState.categoryIds) ||
			JSON.stringify(originalData.tags) !== JSON.stringify(uiState.tags) ||
			// originalData.access.visibility !== uiState.access.visibility ||
			originalData.access.allowDownload !== uiState.access.allowDownload ||
			originalData.access.allowShare !== uiState.access.allowShare ||
			originalData.manage.status !== uiState.manage.status ||
			originalData.manage.publishDate !== uiState.manage.publishDate
			// JSON.stringify(originalData.access.accessControl) !== JSON.stringify(uiState.access.accessControl)
		);
	}, [ originalData, uiState ]);

	// Generic state updater function that accepts a field path and a value or updater function
	const updateState = useCallback(<K extends keyof PodcastUIState>(
		section: K,
		valueOrUpdater: PodcastUIState[K] | ((prev: PodcastUIState[K]) => PodcastUIState[K])
	) =>
	{
		setUiState((prevState) =>
		{
			const newValue = typeof valueOrUpdater === 'function'
				? (valueOrUpdater as Function)(prevState[section])
				: valueOrUpdater;

			return {
				...prevState,
				[section] : newValue
			};
		});
	}, []);

	// Handle updates to details section (title, description, etc.)
	const handleDetailsUpdate = useCallback((detailsData: any) =>
	{
		// ENHANCED: Handle thumbnail ↔ coverImage property mapping for podcast
		// DetailsSlider works with 'thumbnail' but Podcast uses 'coverImage'
		const mappedData = { ...detailsData };

		// Map thumbnail updates from DetailsSlider to coverImage for podcast state
		if (mappedData.thumbnail)
		{
			mappedData.coverImage = mappedData.thumbnail;
			delete mappedData.thumbnail; // Remove thumbnail to avoid confusion
		}

		updateState('details', (prev) => ({
			...prev,
			...mappedData
		}));
	}, [ updateState ]);

	// Handle updates to categories
	const handleCategoriesUpdate = useCallback((categories: string[]) =>
	{
		updateState('categoryIds', categories);
	}, [ updateState ]);

	// Handle updates to tags
	const handleTagsUpdate = useCallback((tags: string[]) =>
	{
		updateState('tags', tags);
	}, [ updateState ]);

	// Handle updates to access control settings
	const handleAccessUpdate = useCallback((accessData: any) =>
	{
		updateState('access', (prev) =>
		{
			const newState = {
				...prev,
				// visibility    : accessData.visibility || prev.visibility,
				allowDownload : accessData.allowDownload !== undefined ? accessData.allowDownload : prev.allowDownload,
				allowShare    : accessData.allowShare !== undefined ? accessData.allowShare : prev.allowShare
				// accessControl : accessData.accessControl || prev.accessControl
			};

			return newState;
		});
	}, [ updateState ]);

	// Enhanced save function to handle multiple podcasts
	const handleSave = useCallback(() =>
	{
		// Create base object without categoryIds to ensure they're never included when editing details
		const baseData = {
			...(uiState.details.title && { title: uiState.details.title }),
			...(uiState.details.description && { description: uiState.details.description }),
			...(uiState.details.sourceType && { sourceType: uiState.details.sourceType }),
			// Conditionally format coverImage based on itemType
			...(uiState.details.coverImage && (
				itemType === 'podcast'
					? {
						// For podcasts, keep the complex object structure
						coverImage : {
							url       : uiState.details.coverImage.url,
							canEdit   : uiState.details.coverImage.canEdit,
							canDelete : uiState.details.coverImage.canDelete,
							...(uiState.details.coverImage.source && { source: uiState.details.coverImage.source }),
							...(uiState.details.coverImage.key && { key: uiState.details.coverImage.key })
						}
					}
					: {
						// For seasons and episodes, use simple string value
						coverImage : uiState.details.coverImage.url
					}
			)),
			...(uiState.tags.length > 0 && { tags: uiState.tags }),
			// ...(uiState.access.visibility && { visibility: uiState.access.visibility }),
			...(uiState.access.allowDownload !== undefined && { allowDownload: uiState.access.allowDownload }),
			...(uiState.access.allowShare !== undefined && { allowShare: uiState.access.allowShare }),
			...(uiState.manage.status && { status: uiState.manage.status })
			// ...(uiState.access.accessControl && { accessControl: uiState.access.accessControl })
		};

		// Prepare final updated data - only include categoryIds if we're on the Categories tab
		const updatedPodcastData = selectedTab === 'Categories'
			? { ...baseData, categoryIds: uiState.categoryIds }
			: baseData;

		// Send data back to parent component
		if (onSave)
		{
			if (selectedItems && selectedItems.length > 0)
			{
				onSave(updatedPodcastData, selectedItems);
			}
			else if (podcastData)
			{
				onSave(updatedPodcastData, [ podcastData.id ]);
			}
		}

		// Close the slider
		setOpen(false);
	}, [ uiState, onSave, setOpen, podcastData, selectedItems, itemType, selectedTab ]);

	// Memoize the tab content to prevent unnecessary re-renders
	const renderTabContent = useMemo(() =>
	{
		switch (selectedTab)
		{
			case 'Details':
				// Only render Details content if a single podcast is selected
				if (selectedItems && selectedItems.length > 1)
				{
					return null;
				}

				// ENHANCED: Map coverImage to thumbnail for DetailsSlider compatibility
				const detailsDataForSlider = {
					...uiState.details,
					// Map coverImage to thumbnail for DetailsSlider
					thumbnail  : uiState.details.coverImage,
					// Remove coverImage to avoid confusion in DetailsSlider
					coverImage : undefined
				};

				return (
					<DetailsSlider
						articleCreate={articleCreate}
						data={detailsDataForSlider}
						onChange={handleDetailsUpdate}
						itemType={itemType}
					/>
				);
			case 'Categories':
				return (
					<CategorySlider
						artcileCreate={articleCreate}
						categories={uiState.categoryIds}
						onChange={handleCategoriesUpdate}
					/>
				);
			case 'Add Tags':
				return (
					<TagSlider
						tags={podcastData?.tags || []}
						initialSelectedTags={uiState.tags}
						onChange={handleTagsUpdate}
					/>
				);
			case 'Access':
				return (
					<AccessSlider
						visibility={''}
						allowDownload={uiState.access.allowDownload}
						allowShare={uiState.access.allowShare}
						accessControl={undefined}
						onChange={handleAccessUpdate}
					/>
				);
			case 'Manage':
				return (
					<ManageContentSlider />
				);
			case 'Activity':
				return (
					<ActivitySlider />
				);
			default:
				return null;
		}
	}, [
		selectedTab,
		selectedItems,
		articleCreate,
		uiState,
		podcastData,
		handleDetailsUpdate,
		handleCategoriesUpdate,
		handleTagsUpdate,
		handleAccessUpdate
	]);

	return (
		<Grid container height='100%' width='100%' >
			{/* Left Sidebar Tabs */}
			<Grid item xs={2.5} height={'100%'} sx={{ boxShadow: '0px 4px 14px 0px rgba(0, 0, 0, 0.10)' }}>
				<Stack spacing={2} pt={3}>
					{podcastTabs.map((tab, index) => (
						<Stack
							key={index}
							direction='column'
							alignItems='center'
							sx={{
								cursor          : 'pointer',
								padding         : '10px 8px',
								borderRight     : selectedTab === tab.name ? '4px solid #24a4c4' : '4px solid transparent',
								backgroundColor : selectedTab === tab.name ? '#EFFBFF' : ''
							}}
							onClick={() => setSelectedTab(tab.name as selectedTabs)}
						>
							{tab?.ImageIcon
								? (
									<Box width={'28px'} height={'28px'}>
										<img src={`${tab.Icon}`} alt='' className='invertGray' />
									</Box>
								)
								: (tab.Icon)}
							<Typography variant='body2' color={'#737373'} pt={0.2}>{tab.name}</Typography>
						</Stack>
					))}
				</Stack>
			</Grid>

			{/* Right Content Area */}
			<Grid item xs={9.5} height={'100%'} width={'100%'}>
				<Grid container height={'100%'} direction={'column'} display={'flex'}>
					<Grid item px={2} py={1}>
						<Stack direction={'row'} alignItems={'center'} display={'flex'} justifyContent={'space-between'}>
							<Typography fontSize={'22px'} fontWeight={'var(--font500)'}>{selectedTab}</Typography>
							<IconButton onClick={() => setOpen(false)}>
								<CloseIcon />
							</IconButton>
						</Stack>
					</Grid>
					<Grid item minHeight={0} pt={1} px={2} pb={1} xs sx={{ overflowY: 'auto' }}>
						{renderTabContent}
					</Grid>
					{selectedTab !== 'Activity' && (
						<Grid item>
							<Stack direction={'row'} p={2} spacing={2} alignItems={'center'} justifyContent={'end'} display={'flex'}>
								<Button onClick={() => setOpen(false)} variant='outlined'>
									{language?.cancel || 'Cancel'}
								</Button>
								<Button
									variant='contained'
									sx={{ color: 'var(--whiteColor)' }}
									onClick={handleSave}
									disabled={!hasChanges}
								>
									{language?.save || 'Save'}
								</Button>
							</Stack>
						</Grid>
					)}
				</Grid>
			</Grid>
		</Grid>
	);
};

export default PodcastDetailsSlider;